import { Flex } from "antd";
import { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import parsePhoneNumber, { AsYouType, isValidPhoneNumber, getExampleNumber } from "libphonenumber-js";
import examples from "libphonenumber-js/examples.mobile.json";
import { PhoneCodeOption } from "./PhoneCodeOption";
import { renderCountryCodeLabel } from "./CountryCodeLabelRenderer";
import { CustomInput, CustomSelect } from "..";
import { fetchCountries, selectCountries, selectCountriesLoading } from "store/countries";

export const validatePhoneNumber = (value) => {
  if (!value) return Promise.resolve();
  if (isValidPhoneNumber(value)) return Promise.resolve();
  return Promise.reject("Invalid phone number");
};

const getDynamicPlaceholder = (countryCode) => {
  try {
    if (!countryCode) return "Enter phone number";
    const example = getExampleNumber(countryCode, examples);
    return example ? example.formatNational().replaceAll(/\d/g, "X") : "XXXXXXXXXX";
  } catch {
    return "XXXXXXXXXX";
  }
};

export const PhoneInput = ({ initialValue, initialCountryCode, disabled, borderless, onChange }) => {
  const dispatch = useDispatch();
  const countries = useSelector(selectCountries);
  const loading = useSelector(selectCountriesLoading);

  useEffect(() => {
    if (!countries || countries.length === 0) {
      dispatch(fetchCountries());
    }
  }, [dispatch, countries?.length]);

  const options = useMemo(() => {
    return (
      countries?.map((country) => ({
        value: country?.code,
        label: <PhoneCodeOption country={country} />,
        phone_code: country?.phone_code,
        name: country?.name,
      })) || []
    );
  }, [countries]);

  const findCountry = (code) => countries?.find((c) => c.code === code) || {};

  const parsedInitialValue = useMemo(() => {
    try {
      return parsePhoneNumber(initialValue || "", initialCountryCode || "US");
    } catch {
      return null;
    }
  }, [initialValue, initialCountryCode]);

  const [countryCode, setCountryCode] = useState(
    initialCountryCode || parsedInitialValue?.country || "US"
  );
  const [phoneNumber, setPhoneNumber] = useState(
    parsedInitialValue?.formatNational({ nationalPrefix: false }) || ""
  );
  const [placeholder, setPlaceholder] = useState("Enter phone number");

  useEffect(() => {
    const dynamicPlaceholder = getDynamicPlaceholder(countryCode);
    setPlaceholder(dynamicPlaceholder);
  }, [countryCode, countries]);

  useEffect(() => {
    if (initialValue || initialCountryCode) {
      try {
        const parsed = parsePhoneNumber(initialValue || "", initialCountryCode || "US");
        setCountryCode(initialCountryCode || parsed?.country || "US");
        setPhoneNumber(parsed?.formatNational({ nationalPrefix: false }) || initialValue);
      } catch {
        setPhoneNumber(initialValue || "");
        setCountryCode(initialCountryCode || "US");
      }
    }
  }, [initialValue, initialCountryCode]);

  const handleCountryCodeSelect = (value) => {
    setCountryCode(value);
    setPhoneNumber("");
    onChange?.({ countryCode: value, phoneNumber: "" });
  };

  const handlePhoneNumberChange = (e) => {
    const input = e.target.value;
    const rawDigits = input.replaceAll(/\D/g, "");

    const country = findCountry(countryCode);
    const allowedLengths = country?.phone_number_lengths || [15];
    const maxLength = Math.max(...allowedLengths);
    const limitedDigits = rawDigits.slice(0, maxLength);

    const dialCode = country?.phone_code || "";
    const fullNumber = `${dialCode}${limitedDigits}`;

    let parsed = null;
    try {
      parsed = parsePhoneNumber(fullNumber);
    } catch (error) {
      console.error("Error parsing phone number:", error);
    }

    let detectedCountry = parsed?.country;
    if (detectedCountry && detectedCountry !== countryCode) {
      const sameDialCountries = countries?.filter((c) => c.phone_code === dialCode);
      if (sameDialCountries?.some((c) => c.code === detectedCountry)) {
        setCountryCode(detectedCountry);
      } else {
        detectedCountry = countryCode;
      }
    }

    const formatted = new AsYouType(detectedCountry || countryCode).input(limitedDigits);
    setPhoneNumber(formatted);

    const isLengthValid = allowedLengths.includes(limitedDigits.length);
    const isValid =
      parsed &&
      isValidPhoneNumber(parsed.number) &&
      isLengthValid &&
      (detectedCountry || countryCode) === countryCode &&
      isValidPhoneNumber(fullNumber);

    onChange?.({
      countryCode,
      fullNumber: isValid ? parsed.number : formatted,
    });
  };

  return (
    <Flex gap={8}>
      <CustomSelect
        options={options}
        value={countryCode}
        onSelect={handleCountryCodeSelect}
        labelRender={renderCountryCodeLabel}
        popupMatchSelectWidth={false}
        showSearch
        filterOption={(input, option) => {
          const name = option?.name?.toLowerCase() || "";
          const dial = option?.phone_code || "";
          return (
            name.includes(input.toLowerCase()) ||
            option?.value?.toLowerCase().includes(input.toLowerCase()) ||
            dial.replaceAll("+", "").includes(input.replaceAll("+", ""))
          );
        }}
        disabled={disabled}
        borderless={borderless}
        style={{ width: "auto" }}
        loading={loading}
      />
      <CustomInput
        value={phoneNumber}
        onChange={handlePhoneNumberChange}
        placeholder={placeholder}
        disabled={disabled}
        borderless={borderless}
        className="flex-grow-1"
        onKeyDown={(event) => {
          if (event.key === "Backspace") {
            const input = event.target.value;
            const cursorPos = event.target.selectionStart;
            if (cursorPos > 0 && /[)\s-]/.test(input[cursorPos - 1])) {
              let newCursor = cursorPos - 1;
              while (newCursor > 0 && /[)\s-(]/.test(input[newCursor - 1])) {
                newCursor--;
              }
              if (/\d/.test(input[newCursor - 1])) {
                const newValue = input.slice(0, newCursor - 1) + input.slice(newCursor);
                event.preventDefault();
                handlePhoneNumberChange({ target: { value: newValue } });
                setTimeout(() => {
                  try {
                    event.target.setSelectionRange(newCursor - 1, newCursor - 1);
                  } catch {}
                }, 0);
                return;
              }
            }
          }
          const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
          const isCtrlCmd = isMac ? event.metaKey : event.ctrlKey;
          const allowedCombo =
            (isCtrlCmd && ["a", "c", "v", "x"].includes(event.key.toLowerCase())) ||
            ["ArrowLeft", "ArrowRight", "Tab"].includes(event.key);
          if (!/\d/.test(event.key) && event.key !== "Backspace" && !allowedCombo) {
            event.preventDefault();
          }
        }}
        onPaste={(e) => {
          const pasted = e.clipboardData.getData("text").replaceAll(/\D/g, "");
          e.preventDefault();
          handlePhoneNumberChange({ target: { value: pasted } });
        }}
      />
    </Flex>
  );
};

PhoneInput.propTypes = {
  initialValue: PropTypes.string,
  initialCountryCode: PropTypes.string,
  disabled: PropTypes.bool,
  borderless: PropTypes.bool,
  onChange: PropTypes.func,
};
